import type { ChangeEvent, ClipboardEvent, KeyboardEvent } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Input } from '@shared/ui'

interface VerificationCodeInputProps {
  readonly autoFocus?: boolean
  readonly ariaLabel?: string
  readonly disabled?: boolean
  readonly digitLabel?: string
  readonly length?: number
  readonly onChange: (value: string) => void
  readonly onComplete?: (value: string) => void
  readonly value: string
}

function normalizeCode(value: string, length: number) {
  return value.replace(/\D/g, '').slice(0, length)
}

export function VerificationCodeInput({
  autoFocus = false,
  ariaLabel = 'Verification code',
  digitLabel = 'digit',
  disabled = false,
  length = 6,
  onChange,
  onComplete,
  value,
}: VerificationCodeInputProps) {
  const refs = useRef<Array<HTMLInputElement | null>>([])
  const digits = useMemo(() => {
    const normalized = normalizeCode(value, length)
    return Array.from({ length }, (_, index) => normalized[index] ?? '')
  }, [length, value])
  const [focusedIndex, setFocusedIndex] = useState(0)

  useEffect(() => {
    if (autoFocus) {
      refs.current[0]?.focus()
    }
  }, [autoFocus])

  const commitValue = (nextDigits: readonly string[], nextFocusIndex: number) => {
    const nextValue = nextDigits.join('')
    onChange(nextValue)

    if (nextValue.length === length) {
      onComplete?.(nextValue)
      return
    }

    refs.current[Math.max(0, Math.min(length - 1, nextFocusIndex))]?.focus()
  }

  const handleChange = (index: number, event: ChangeEvent<HTMLInputElement>) => {
    const input = normalizeCode(event.target.value, length)

    if (!input) {
      const nextDigits = [...digits]
      nextDigits[index] = ''
      commitValue(nextDigits, index)
      return
    }

    const nextDigits = [...digits]
    for (let offset = 0; offset < input.length && index + offset < length; offset += 1) {
      nextDigits[index + offset] = input[offset]
    }
    commitValue(nextDigits, index + input.length)
  }

  const handlePaste = (index: number, event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = normalizeCode(event.clipboardData.getData('text'), length)

    if (!pasted) {
      return
    }

    event.preventDefault()
    const nextDigits = [...digits]
    for (let offset = 0; offset < pasted.length && index + offset < length; offset += 1) {
      nextDigits[index + offset] = pasted[offset]
    }
    commitValue(nextDigits, index + pasted.length)
  }

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      event.preventDefault()
      const nextDigits = [...digits]
      nextDigits[index - 1] = ''
      commitValue(nextDigits, index - 1)
      return
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault()
      refs.current[index - 1]?.focus()
    }

    if (event.key === 'ArrowRight' && index < length - 1) {
      event.preventDefault()
      refs.current[index + 1]?.focus()
    }
  }

  return (
    <div aria-label={ariaLabel} className="flex justify-center gap-2" role="group">
      {digits.map((digit, index) => (
        <Input
          aria-label={`${ariaLabel} ${digitLabel} ${index + 1}`}
          className="h-11 w-10 px-0 text-center text-base font-bold tabular-nums sm:w-11"
          disabled={disabled}
          fullWidth={false}
          inputMode="numeric"
          key={index}
          maxLength={length}
          onChange={(event: ChangeEvent<HTMLInputElement>) => handleChange(index, event)}
          onFocus={() => setFocusedIndex(index)}
          onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => handleKeyDown(index, event)}
          onPaste={(event: ClipboardEvent<HTMLInputElement>) => handlePaste(index, event)}
          pattern="[0-9]*"
          ref={(element) => {
            refs.current[index] = element
          }}
          value={focusedIndex === index ? digit : digit}
        />
      ))}
    </div>
  )
}
